// The AC-130: thirty seconds in the gunner's seat. The view goes white-hot thermal and zooms in,
// drifting slowly round as the gunship flies its orbit; the crosshair of the gun in hand sits in
// the middle of the screen. With the pointer locked, moving the mouse slews the sight across the
// ground; without a lock (a touch screen, or the lock refused) the sight follows the pointer.
// Click to fire, hold for the 25 mm; 1, 2 and 3 or the wheel pick the 105, the 40 and the 25.
// Every round goes through the sim as a logged command, so a saved game replays it.
import { GUN_WEAPONS, GUNSHIP_WEAPONS, TICKS_PER_SECOND, TILE, type GunWeapon, type TdSim } from '@thrash/sim';
import type { Container } from 'pixi.js';
import type { Camera } from './camera.ts';
import { toTile } from './iso.ts';
import { el } from './hud.ts';
import { is3D, setThermal3D } from './view3d.ts';
import { haptic } from './haptics.ts';

/** How much closer the sight is than the view the player left. */
const ZOOM = 1.7;
/** The orbit: how far the view drifts round, in screen pixels, and how long a lap takes. */
const ORBIT_PX = 46;
const ORBIT_MS = 24000;
/** The crosshair for each gun: brackets for the 105, a box for the 40, a fine cross for the 25. */
const CROSS: Record<GunWeapon, string> = {
  '105': '<path d="M-90 -60 h-20 v120 h20 M90 -60 h20 v120 h-20 M-30 0 h-40 M30 0 h40 M0 -30 v-40 M0 30 v40"/>',
  '40': '<path d="M-36 -36 h72 v72 h-72 z M-12 0 h-34 M12 0 h34 M0 -12 v-34 M0 12 v34"/>',
  '25': '<path d="M-8 0 h-26 M8 0 h26 M0 -8 v-26 M0 8 v26"/><circle r="2"/>',
};

export class Gunship {
  private weapon: GunWeapon = '105';
  private firing = false;
  private locked = false;
  private pointer: [number, number];
  private readonly started = performance.now();
  private orbitOff: [number, number] = [0, 0];
  private readonly saved: { scale: number; x: number; y: number };
  private readonly root: HTMLElement;
  private readonly cross: HTMLElement;
  private readonly time: HTMLElement;
  private readonly guns = new Map<GunWeapon, { row: HTMLElement; bar: HTMLElement }>();
  private readonly onMove = (e: MouseEvent): void => {
    if (this.locked) this.camera.panBy(-e.movementX, -e.movementY);
    else this.pointer = [e.clientX, e.clientY];
  };
  private readonly onLock = (): void => { this.locked = document.pointerLockElement === this.canvas; };
  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    e.stopImmediatePropagation();
    const i = GUN_WEAPONS.indexOf(this.weapon);
    this.pick(GUN_WEAPONS[(i + (e.deltaY > 0 ? 1 : GUN_WEAPONS.length - 1)) % GUN_WEAPONS.length]!);
  };

  constructor(private readonly sim: TdSim, private readonly camera: Camera, private readonly world: Container, private readonly canvas: HTMLCanvasElement) {
    this.pointer = [window.innerWidth / 2, window.innerHeight / 2];
    this.saved = { scale: world.scale.x, x: world.position.x, y: world.position.y };
    camera.zoomAt(window.innerWidth / 2, window.innerHeight / 2, ZOOM);
    setThermal3D(true);
    // the flat look has no thermal pass: its canvas is greyed and pushed hard instead
    if (!is3D()) this.canvas.style.filter = 'grayscale(1) contrast(1.9) brightness(1.1)';

    this.root = el('div', 'gunship');
    this.root.setAttribute('aria-label', 'AC-130 gunner station');
    this.cross = el('div', 'gs-cross');
    const tl = el('div', 'gs-tl');
    tl.append(el('span', '', 'AC-130  ·  WHITE HOT'), el('span', '', 'ANGEL 9  ·  ORBIT LEFT'));
    const tr = el('div', 'gs-tr');
    this.time = el('span', 'gs-time');
    tr.append(this.time, el('span', '', 'ON STATION'));
    const guns = el('div', 'gs-guns');
    for (const w of GUN_WEAPONS) {
      const row = el('div', 'gs-gun');
      const bar = el('i');
      const track = el('span', 'bar');
      track.appendChild(bar);
      row.append(el('span', '', `${GUN_WEAPONS.indexOf(w) + 1}  ${GUNSHIP_WEAPONS[w].name.toUpperCase()}`), track);
      guns.appendChild(row);
      this.guns.set(w, { row, bar });
    }
    const help = el('div', 'gs-help');
    help.innerHTML = 'MOUSE AIM · CLICK FIRE · HOLD FOR 25 MM<br>1 2 3 / WHEEL: GUN';
    this.root.append(this.cross, tl, tr, guns, help);
    document.body.appendChild(this.root);
    this.pick('105');

    window.addEventListener('mousemove', this.onMove);
    document.addEventListener('pointerlockchange', this.onLock);
    window.addEventListener('wheel', this.onWheel, { passive: false, capture: true });
    // the call-in was a click, so the browser lets the sight take the mouse
    try { void (this.canvas.requestPointerLock() as unknown as Promise<void> | undefined)?.catch?.(() => { /* refused: the sight follows the pointer */ }); } catch { /* no pointer lock here */ }
  }

  /** Ticks left overhead. */
  get left(): number {
    return this.sim.gunshipLeft();
  }

  pick(w: GunWeapon): void {
    this.weapon = w;
    this.cross.innerHTML = `<svg width="1" height="1" viewBox="0 0 1 1">${CROSS[w]}</svg>`;
    for (const [k, g] of this.guns) g.row.classList.toggle('on', k === w);
  }

  key(k: string): boolean {
    const i = ['1', '2', '3'].indexOf(k);
    if (i < 0) return false;
    this.pick(GUN_WEAPONS[i]!);
    return true;
  }

  pointerDown(sx: number, sy: number, button: number): void {
    if (button !== 0) return;
    if (!this.locked) this.pointer = [sx, sy];
    // a click after the lock was let go (Esc) takes the mouse again
    if (!this.locked && document.pointerLockElement !== this.canvas) {
      try { void (this.canvas.requestPointerLock() as unknown as Promise<void> | undefined)?.catch?.(() => {}); } catch { /* none */ }
    }
    this.firing = true;
    this.fire();
  }

  pointerUp(): void {
    this.firing = false;
  }

  /** Where the sight is on the screen: the middle with the mouse locked, else under the pointer. */
  private sight(): [number, number] {
    return this.locked ? [window.innerWidth / 2, window.innerHeight / 2] : this.pointer;
  }

  private fire(): void {
    const [sx, sy] = this.sight();
    const p = this.world.toLocal({ x: sx, y: sy });
    const [fx, fy] = toTile(p.x, p.y);
    if (this.sim.gunshipFire(this.weapon, fx * TILE, fy * TILE)) {
      if (this.weapon !== '25') haptic('medium');
      // the 105's recoil kicks the sight
      if (this.weapon === '105') this.root.animate([{ transform: 'translate(0, 6px)' }, { transform: 'none' }], { duration: 220, easing: 'ease-out' });
    }
  }

  /** Every frame: the orbit's drift, the 25 mm held down, the readouts. Returns false once the gunship has gone. */
  update(): boolean {
    const t = ((performance.now() - this.started) / ORBIT_MS) * Math.PI * 2;
    const off: [number, number] = [Math.cos(t) * ORBIT_PX - ORBIT_PX, Math.sin(t) * ORBIT_PX * 0.6];
    this.camera.panBy(off[0] - this.orbitOff[0], off[1] - this.orbitOff[1]);
    this.orbitOff = off;
    if (this.firing && this.weapon === '25') this.fire();
    this.cross.style.left = `${this.sight()[0]}px`;
    this.cross.style.top = `${this.sight()[1]}px`;
    const left = this.left;
    this.time.textContent = `00:${String(Math.ceil(left / TICKS_PER_SECOND)).padStart(2, '0')}`;
    for (const [w, g] of this.guns) {
      const total = GUNSHIP_WEAPONS[w].reloadTicks;
      const k = total > 0 ? 1 - this.sim.gunReadyIn(w) / total : 1;
      g.bar.style.width = `${Math.round(k * 100)}%`;
    }
    this.cross.classList.toggle('gs-firing', this.firing);
    return left > 0;
  }

  /** The gunship goes: the mouse is let go, the sight comes down and the view goes back to where it was. */
  end(): void {
    window.removeEventListener('mousemove', this.onMove);
    document.removeEventListener('pointerlockchange', this.onLock);
    window.removeEventListener('wheel', this.onWheel, { capture: true });
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.root.remove();
    setThermal3D(false);
    if (!is3D()) this.canvas.style.filter = '';
    this.world.scale.set(this.saved.scale);
    this.world.position.set(this.saved.x, this.saved.y);
  }
}
